export class LiveKitAudioTransport {
    room;
    events = null;
    subscriptions = [];
    attachedTracks = new Set();
    audioElements = new Set();
    speakerEnabled = true;
    constructor(room) {
        this.room = room;
    }
    async connect(grant, events) {
        await this.disconnect();
        this.events = events;
        this.subscribe("activeSpeakersChanged", (...arguments_) => {
            const speakers = Array.isArray(arguments_[0]) ? arguments_[0] : [];
            const active = new Set(speakers.flatMap((participant) => participant.identity ? [participant.identity] : []));
            for (const participant of this.room.remoteParticipants.values()) {
                if (participant.identity)
                    events.onSpeakingChanged(participant.identity, active.has(participant.identity));
            }
        });
        this.subscribe("connectionQualityChanged", (...arguments_) => {
            const quality = typeof arguments_[0] === "string" ? arguments_[0] : "unknown";
            events.onNetworkQualityChanged(mapQuality(quality));
        });
        this.subscribe("disconnected", (...arguments_) => events.onDisconnected(typeof arguments_[0] === "string" ? arguments_[0] : "disconnected"));
        this.subscribe("trackSubscribed", (...arguments_) => {
            const track = arguments_[0];
            this.attachAudioTrack(track);
        });
        this.subscribe("trackUnsubscribed", (...arguments_) => {
            this.detachAudioTrack(arguments_[0]);
        });
        this.subscribe("trackPublished", (...arguments_) => {
            const publication = arguments_[0];
            this.applySpeakerPreferenceToPublication(publication);
        });
        try {
            await this.room.connect(grant.serverUrl, grant.token, { autoSubscribe: true });
            if (this.room.name && this.room.name !== grant.roomName)
                throw new Error("LiveKit connected to an unexpected table room");
            await this.room.startAudio?.();
            await this.room.localParticipant.setMicrophoneEnabled(false);
            this.applySpeakerPreferenceToRemoteParticipants();
            events.onNetworkQualityChanged(mapQuality(this.room.localParticipant.connectionQuality ?? "unknown"));
        }
        catch (error) {
            const normalized = error instanceof Error ? error : new Error("Unable to connect table audio");
            if (/permission|notallowed|microphone/i.test(normalized.message))
                events.onPermissionError(normalized);
            await this.disconnect();
            throw normalized;
        }
    }
    async setMicrophoneEnabled(enabled) {
        try {
            await this.room.localParticipant.setMicrophoneEnabled(enabled);
        }
        catch (error) {
            const normalized = error instanceof Error ? error : new Error("Unable to change microphone state");
            this.events?.onPermissionError(normalized);
            throw normalized;
        }
    }
    setSpeakerEnabled(enabled) {
        this.speakerEnabled = enabled;
        this.applySpeakerPreferenceToRemoteParticipants();
        for (const element of this.audioElements) {
            element.muted = !enabled;
            element.volume = enabled ? 1 : 0;
        }
        return Promise.resolve();
    }
    async disconnect() {
        try {
            await this.room.localParticipant.setMicrophoneEnabled(false);
        }
        catch { /* Track may already be gone. */ }
        for (const { event, listener } of this.subscriptions)
            this.room.off?.(event, listener);
        this.subscriptions.length = 0;
        for (const track of [...this.attachedTracks])
            this.detachAudioTrack(track);
        for (const element of this.audioElements)
            element.remove();
        this.audioElements.clear();
        await this.room.disconnect(true);
        this.events = null;
    }
    subscribe(event, listener) {
        this.room.on?.(event, listener);
        this.subscriptions.push({ event, listener });
    }
    applySpeakerPreferenceToRemoteParticipants() {
        for (const participant of this.room.remoteParticipants.values()) {
            const publications = participant.audioTrackPublications ?? participant.trackPublications ?? new Map();
            for (const publication of publications.values()) {
                this.applySpeakerPreferenceToPublication(publication);
                this.attachAudioTrack(publication.track ?? undefined);
            }
        }
    }
    applySpeakerPreferenceToPublication(publication) {
        if (publication?.kind !== undefined && publication.kind !== "audio")
            return;
        this.applySpeakerPreferenceToTrack(publication?.track ?? undefined);
    }
    applySpeakerPreferenceToTrack(track) {
        if (track?.kind !== undefined && track.kind !== "audio")
            return;
        track?.setVolume?.(this.speakerEnabled ? 1 : 0);
    }
    attachAudioTrack(track) {
        if (!track || (track.kind !== undefined && track.kind !== "audio"))
            return;
        this.applySpeakerPreferenceToTrack(track);
        if (this.attachedTracks.has(track) || !track.attach || typeof document === "undefined")
            return;
        const element = track.attach();
        element.autoplay = true;
        element.controls = false;
        element.muted = !this.speakerEnabled;
        element.style.display = "none";
        element.dataset.guandanTableAudio = "true";
        document.body.append(element);
        this.attachedTracks.add(track);
        this.audioElements.add(element);
        void element.play().catch(() => undefined);
    }
    detachAudioTrack(track) {
        if (!track || !this.attachedTracks.has(track))
            return;
        for (const element of track.detach?.() ?? []) {
            this.audioElements.delete(element);
            element.remove();
        }
        this.attachedTracks.delete(track);
    }
}
function mapQuality(value) {
    switch (value.toLowerCase()) {
        case "excellent": return "excellent";
        case "good": return "good";
        case "poor": return "poor";
        case "lost": return "lost";
        default: return "unknown";
    }
}
//# sourceMappingURL=livekit-transport.js.map